import { Divider, Stack } from "@mui/material";
import { format, isToday, isTomorrow } from "date-fns";
import it from "date-fns/locale/it";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AgendaTab,
  CardsList,
  DayHeader,
  DaySection,
  DaySubtitle,
  DayTitle,
  EventsList,
  NoEventsText,
  TabPanel,
  TabsContainer,
  TabsRoot
} from "./AgendaEventsTabs.styles";
import { AgendaEventCard, type DayEvents, Spinner } from "../../components";
import { switchLocaleImport } from "../../components/Calendar/LocaleLogic";
import { useLanguageStore } from "../../stores";

export type AgendaEventsTab = {
  id: string;
  label: string;
  cards: DayEvents[][];
};

type AgendaEventsTabsProps = {
  tabs: AgendaEventsTab[];
  isLoading: boolean;
  firstSmartCourseId?: number;
  selectAction: (action: string, date: Date, slotId: number, start: string, end: string) => void;
  scrollAction: () => void;
  setShowSmartLearning: () => void;
};

export function AgendaEventsTabs({
  tabs,
  isLoading,
  selectAction,
  setShowSmartLearning
}: AgendaEventsTabsProps) {
  const [selectedTab, setSelectedTab] = useState(tabs[0]?.id ?? "");
  const { t } = useTranslation();
  const language = useLanguageStore(state => state.language)?.id;
  const [locale, setLocale] = useState<Locale>(it);

  useEffect(() => {
    (async () => {
      try {
        const newLocale = await switchLocaleImport(language);
        setLocale(newLocale.default);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("import date-fns locale:", err);
      }
    })();
  }, [language]);

  function flattenDayEvents(cards: DayEvents[][]): DayEvents[] {
    const dayMap = new Map<string, DayEvents>();
    for (const page of cards) {
      for (const day of page) {
        const key = day.date.join("/");
        const existing = dayMap.get(key);
        if (existing) {
          existing.courses.push(...day.courses);
        } else {
          dayMap.set(key, { courses: [...day.courses], date: day.date });
        }
      }
    }
    return Array.from(dayMap.values());
  }

  function getDayLabel(date: Date): string {
    if (isToday(date)) return t("today");
    if (isTomorrow(date)) return t("tomorrow");
    return format(date, "EEEE", { locale });
  }

  return (
    <TabsRoot>
      <TabsContainer
        variant="scrollable"
        scrollButtons={ true }
        allowScrollButtonsMobile
        value={ selectedTab }
        aria-label={ t("agenda_events_tabs_label") }
      >
        { tabs.map((tab, index) => (
          <AgendaTab
            key={ index }
            label={ tab.label }
            value={ tab.id }
            onClick={ () => setSelectedTab(tab.id) }
            id={ `agenda-tab-${tab.id}` }
            aria-controls={ `agenda-tabpanel-${tab.id}` }
            tabIndex={ 0 }
            aria-label={ tab.label }
          />
        )) }
      </TabsContainer>
      { tabs.map((tab) => {
        const dayEvents = flattenDayEvents(tab.cards);

        return (
          <TabPanel
            key={ tab.id }
            tabIndex={ -1 }
            role="tabpanel"
            hidden={ tab.id !== selectedTab }
            id={ `agenda-tabpanel-${tab.id}` }
            aria-label={ tab.label }
            aria-labelledby={ `agenda-tab-${tab.id}` }
          >
            { isLoading
              ? <Spinner />
              : (
                <EventsList>
                  { dayEvents.map((day, indx) => {
                    const dateObj = new Date(day.date.toString().replaceAll(",", "/"));
                    const dayLabel = getDayLabel(dateObj);
                    const dateStr = format(dateObj, "d MMMM yyyy", { locale });
                    const activeCourses = day.courses.filter(
                      (c) => c.dayCourseType !== "empty"
                    );
                    const eventCount = activeCourses.length;

                    return (
                      <DaySection key={ day.date.join("-") }>
                        <DayHeader>
                          <Stack 
                            flexDirection={ "row" }
                            gap="0.5rem"
                          >
                            <DayTitle>
                              { dayLabel }
                            </DayTitle>
                            <DayTitle $fontWeight={ 400 }>
                              |
                            </DayTitle>
                            <DayTitle $fontWeight={ 400 }>
                              { dateStr }
                            </DayTitle>
                          </Stack>
                          <DaySubtitle>
                            { eventCount > 0
                              ? `${eventCount} ${eventCount === 1 ? t("event_singular") : t("events_plural")}`
                              : ""
                            }
                          </DaySubtitle>
                        </DayHeader>
                        { eventCount > 0
                          ? (
                            <CardsList>
                              { activeCourses.map((course) => {
                                if (course.dayCourseType === "empty") return null;
                                return (
                                  <AgendaEventCard
                                    key={ `event-${course.id}` }
                                    course={ course }
                                    selectAction={ selectAction }
                                    setShowSmartLearning={ setShowSmartLearning }
                                    accesibiltyLabel={ `${dayLabel} ${dateStr}` }
                                  />
                                );
                              }) }
                            </CardsList>
                          )
                          : <NoEventsText>{ t("no_scheduled_event") }</NoEventsText>
                        }
                        { indx < dayEvents.length -1 && (
                          <Divider sx={ {
                            margin: "0.5rem"
                          } } />
                        ) }
                      </DaySection>
                    );
                  }) }
                </EventsList>
              )
            }
          </TabPanel>
        );
      }) }
    </TabsRoot>
  );
}
